import { useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from '@ekhodealer/ekho-common/rev';

import type { DiscountCampaign } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { discountsEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Input type matching the backend CreateDiscountBodySchema.
 * Accepts form-shaped data that mirrors the admin portal discount form.
 */
export interface CreateDiscountInput {
	name: string;
	discountType: string;
	amountType: 'fixed' | 'percentage';
	method: 'code' | 'automatic';
	code?: string;
	amount: number;
	appliesTo: 'all' | 'specific';
	applicableProducts: string[];
	startsAt: number;
	endsAt?: number;
	hasEndDate: boolean;
}

/**
 * API function to create a new discount
 */
const createDiscount = async (
	servicesProviderId: string,
	input: CreateDiscountInput,
): Promise<DiscountCampaign> => {
	const response = await apiClient.post<DiscountCampaign>(
		discountsEndpoints.create(servicesProviderId),
		input,
	);
	return response.data;
};

/**
 * Hook for creating a new discount
 * Invalidates all discounts queries on success so the table and search results refresh
 */
export const useCreateDiscount = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: CreateDiscountInput) => {
			if (!servicesProviderId) {
				throw new Error('Cannot create discount without services provider ID');
			}
			return createDiscount(servicesProviderId, input);
		},
		onSuccess: () => {
			toast({
				message: 'Discount created successfully',
				variant: 'success',
			});

			// Invalidate all discounts queries (search/list, details, etc.)
			// so the table refreshes with the new discount
			queryClient.invalidateQueries({
				queryKey: queries.discounts._def,
			});
		},
		onError: (error) => {
			toast({
				message: `Failed to create discount: ${(error as Error).message}`,
				variant: 'error',
			});
		},
	});
};
